import { useState, type SVGProps } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Heading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchInput,
  SelectField,
  Text,
  tabularNumsClass,
  type DataTableColumn,
} from "@lendcord/concord";
import { useListApplicationsQuery } from "./api/onboarding.api";
import { formatTimestampDate } from "@/app/dateFormat";
import type { ApplicationListItem, ApplicationStatus, ApplicationType } from "./onboarding.types";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const PAGE_SIZE = 10;

const STATUS_BADGE: Record<string, "success" | "warning" | "info" | "error"> = {
  PENDING: "warning",
  IN_PROGRESS: "info",
  APPROVED: "success",
  REJECTED: "error",
  DELETED: "error",
  CANCELLED: "info",
};

const STATUS_FILTER_OPTIONS: ApplicationStatus[] = ["PENDING", "IN_PROGRESS", "APPROVED", "REJECTED", "DELETED"];

// The 5 columns the header wants sortable — every other column (location,
// remarks, action) stays a plain static header, sorting by those isn't
// meaningful (location/remarks are free text, action isn't a data field).
type SortKey = "readableId" | "fullName" | "loanAmount" | "createdAt" | "status";
type SortDirection = "asc" | "desc";

function formatDate(iso: string) {
  return formatTimestampDate(iso);
}

// loanAmount's null case is handled outside the `* dir` multiplier
// deliberately — "no loan amount" (LENDER applications, or a BORROWER
// submitted without one) should sort last regardless of ascending/descending,
// not flip to the front when the direction reverses.
function compareApplications(a: ApplicationListItem, b: ApplicationListItem, key: SortKey, direction: SortDirection) {
  const dir = direction === "asc" ? 1 : -1;
  switch (key) {
    case "loanAmount": {
      if (a.loanAmount == null && b.loanAmount == null) return 0;
      if (a.loanAmount == null) return 1;
      if (b.loanAmount == null) return -1;
      return (a.loanAmount - b.loanAmount) * dir;
    }
    case "createdAt":
      return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dir;
    case "status":
      return a.status.localeCompare(b.status) * dir;
    case "readableId":
      return (a.readableId ?? "").localeCompare(b.readableId ?? "") * dir;
    case "fullName":
      return (a.fullName ?? "").localeCompare(b.fullName ?? "") * dir;
  }
}

function EyeIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function PencilIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M17 3a2.85 2.65 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="M15 5l4 4" />
    </svg>
  );
}

function ChevronUpIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

function ChevronDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

// Unsorted-state indicator — a faint double chevron, standard "this column
// is sortable, click to sort" affordance (GitHub/Notion/Linear-style tables).
function ChevronsUpDownIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />
    </svg>
  );
}

function FilterIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}

interface SortableHeaderProps {
  label: string;
  sortKey: SortKey;
  activeSortKey: SortKey | null;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
}

function SortableHeader({ label, sortKey, activeSortKey, direction, onSort }: SortableHeaderProps) {
  const isActive = activeSortKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1 whitespace-nowrap hover:text-text ${isActive ? "text-text" : ""}`}
      aria-label={label}
    >
      {label}
      {isActive ? (
        direction === "asc" ? (
          <ChevronUpIcon className="h-3.5 w-3.5" />
        ) : (
          <ChevronDownIcon className="h-3.5 w-3.5" />
        )
      ) : (
        <ChevronsUpDownIcon className="h-3.5 w-3.5 text-text-subtle" />
      )}
    </button>
  );
}

interface ApplicationsListProps {
  onAddNew: () => void;
  onView: (application: ApplicationListItem) => void;
  onEdit: (application: ApplicationListItem) => void;
}

export function ApplicationsList({ onAddNew, onView, onEdit }: ApplicationsListProps) {
  const intl = useIntl();
  const { data: applications, isLoading } = useListApplicationsQuery();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [typeFilter, setTypeFilter] = useState<ApplicationType | "">("");
  const [statusFilter, setStatusFilter] = useState<ApplicationStatus | "">("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  }

  const hasActiveFilters = typeFilter !== "" || statusFilter !== "";
  const activeFilterCount = (typeFilter !== "" ? 1 : 0) + (statusFilter !== "" ? 1 : 0);

  function handleClearFilters() {
    setTypeFilter("");
    setStatusFilter("");
    setPage(1);
  }

  const searched = applications
    ? applications.filter((app) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return (
          app.fullName?.toLowerCase().includes(q) ||
          app.readableId?.toLowerCase().includes(q) ||
          app.applicationType.toLowerCase().includes(q) ||
          app.locationAddress?.toLowerCase().includes(q) ||
          app.remarks?.toLowerCase().includes(q)
        );
      })
    : null;

  const typeFiltered = searched && typeFilter ? searched.filter((app) => app.applicationType === typeFilter) : searched;
  const statusFiltered = typeFiltered && statusFilter ? typeFiltered.filter((app) => app.status === statusFilter) : typeFiltered;
  const filtered = statusFiltered && sortKey
    ? [...statusFiltered].sort((a, b) => compareApplications(a, b, sortKey, sortDirection))
    : statusFiltered;

  const totalCount = filtered?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const pageItems = filtered?.slice(startIndex, startIndex + PAGE_SIZE) ?? [];
  const rangeStart = totalCount === 0 ? 0 : startIndex + 1;
  const rangeEnd = Math.min(startIndex + PAGE_SIZE, totalCount);

  const columns: DataTableColumn<ApplicationListItem>[] = [
    // ID leads the table (the reference/lookup column); the mobile card
    // still leads with fullName (titleColumnId below) since a name reads
    // faster than an ID in a scrolling card list — table and card are
    // allowed to prioritize differently, DataTable doesn't require them to
    // agree. Status stays last in the table (matches BorrowersList/
    // EmiDashboard's convention) but is still pinned to the card heading on
    // mobile via metaColumnId.
    {
      id: "readableId",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.onboarding.columnReadableId" })}
          sortKey="readableId"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (app) => (
        <button
          type="button"
          onClick={() => onView(app)}
          className="font-medium text-accent-600 hover:underline"
        >
          {app.readableId}
        </button>
      ),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "fullName",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.onboarding.columnFullName" })}
          sortKey="fullName"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (app) => app.fullName,
      cellClassName: "whitespace-nowrap font-medium text-text",
    },
    {
      id: "applicationType",
      header: intl.formatMessage({ id: "lender.onboarding.columnApplicationType" }),
      cell: (app) => <span className="capitalize">{app.applicationType.toLowerCase()}</span>,
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "loanAmount",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.onboarding.columnLoanAmount" })}
          sortKey="loanAmount"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (app) => (app.loanAmount != null ? currencyFormatter.format(app.loanAmount) : "—"),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "location",
      header: intl.formatMessage({ id: "lender.onboarding.columnLocation" }),
      cell: (app) => app.locationAddress ?? "—",
      cellClassName: "whitespace-nowrap max-w-48 truncate",
    },
    {
      id: "createdAt",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.onboarding.columnCreatedAt" })}
          sortKey="createdAt"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (app) => formatDate(app.createdAt),
      cellClassName: `whitespace-nowrap ${tabularNumsClass}`,
    },
    {
      id: "remarks",
      header: intl.formatMessage({ id: "lender.onboarding.columnRemarks" }),
      cell: (app) => app.remarks ?? "—",
      cellClassName: "whitespace-nowrap max-w-40 truncate",
    },
    {
      id: "status",
      header: (
        <SortableHeader
          label={intl.formatMessage({ id: "lender.onboarding.columnStatus" })}
          sortKey="status"
          activeSortKey={sortKey}
          direction={sortDirection}
          onSort={handleSort}
        />
      ),
      cell: (app) => (
        <Badge status={STATUS_BADGE[app.status] ?? "info"}>
          <FormattedMessage id={`lender.onboarding.status${app.status}`} />
        </Badge>
      ),
      cellClassName: "whitespace-nowrap",
    },
    {
      id: "action",
      header: intl.formatMessage({ id: "lender.onboarding.columnAction" }),
      cell: (app) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onView(app)}
            aria-label={intl.formatMessage({ id: "lender.onboarding.actionViewLink" })}
            className="text-text-muted hover:text-accent-600"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          {app.isEditAllowed && (
            <button
              type="button"
              onClick={() => onEdit(app)}
              aria-label={intl.formatMessage({ id: "lender.onboarding.actionEditLink" })}
              className="text-text-muted hover:text-accent-600"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
      cellClassName: "whitespace-nowrap",
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>
          <FormattedMessage id="lender.onboarding.applicationsListHeading" />
        </Heading>
        <Button type="button" onClick={onAddNew}>
          <FormattedMessage id="lender.onboarding.addNewCustomerButton" />
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="max-w-sm flex-1">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClear={() => {
              setQuery("");
              setPage(1);
            }}
            placeholder={intl.formatMessage({ id: "lender.onboarding.searchApplicationsPlaceholder" })}
          />
        </div>

        <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative inline-flex items-center gap-2 rounded-sm border border-border bg-surface-raised px-3 py-2 text-sm font-medium text-text hover:bg-surface"
            >
              <FilterIcon className="h-4 w-4" />
              <FormattedMessage id="lender.onboarding.filtersButton" />
              {activeFilterCount > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-600 text-[10px] font-semibold text-on-accent">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex flex-col gap-4">
            <SelectField
              label={intl.formatMessage({ id: "lender.onboarding.columnApplicationType" })}
              placeholder={intl.formatMessage({ id: "lender.onboarding.filterAllTypes" })}
              value={typeFilter}
              onValueChange={(value) => {
                setTypeFilter(value as ApplicationType);
                setPage(1);
              }}
              options={[
                { value: "BORROWER", label: intl.formatMessage({ id: "lender.onboarding.applicationTypeBorrower" }) },
                { value: "LENDER", label: intl.formatMessage({ id: "lender.onboarding.applicationTypeLender" }) },
                { value: "STAFF", label: intl.formatMessage({ id: "lender.onboarding.applicationTypeStaff" }) },
                { value: "AGENT", label: intl.formatMessage({ id: "lender.onboarding.applicationTypeAgent" }) },
              ]}
            />
            <SelectField
              label={intl.formatMessage({ id: "lender.onboarding.columnStatus" })}
              placeholder={intl.formatMessage({ id: "lender.onboarding.filterAllStatuses" })}
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as ApplicationStatus);
                setPage(1);
              }}
              options={STATUS_FILTER_OPTIONS.map((status) => ({
                value: status,
                label: intl.formatMessage({ id: `lender.onboarding.status${status}` }),
              }))}
            />
            <div className="flex justify-end">
              <Button type="button" variant="ghost" density="compact" onClick={handleClearFilters} disabled={!hasActiveFilters}>
                <FormattedMessage id="lender.onboarding.clearFiltersButton" />
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <DataTable
        columns={columns}
        rows={pageItems}
        rowKey={(app) => app.id}
        loading={isLoading}
        titleColumnId="fullName"
        metaColumnId="status"
        emptyState={
          <EmptyState>
            <FormattedMessage id={hasActiveFilters ? "lender.onboarding.noMatchingApplicationsMessage" : "lender.onboarding.emptyStateMessage"} />
          </EmptyState>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Text size="sm" variant="muted">
          <FormattedMessage id="lender.creditor.rowsPerPageLabel" values={{ count: PAGE_SIZE }} />
        </Text>
        <div className="flex items-center gap-3">
          <Text size="sm" variant="muted">
            <FormattedMessage
              id="lender.creditor.paginationRangeLabel"
              values={{ start: rangeStart, end: rangeEnd, total: totalCount }}
            />
          </Text>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.previousPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ‹
          </button>
          <button
            type="button"
            disabled={page >= pageCount}
            onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
            aria-label={intl.formatMessage({ id: "lender.shared.nextPageLabel" })}
            className="text-text-muted disabled:opacity-40"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
